import {
  AdmitData,
  HUV1Data,
  HUV2Data,
  DischargeData,
} from '@mummssoftware/utils/hope';
import { ColDef } from 'ag-grid-community';
import defaults from '../../translations/en.json';
import { HopeBatchMode } from '../Context/constants';
import {
  ButtonCellRenderer,
  getButtonCellStyle,
  getTextCellStyle,
  getNumberCellStyle,
} from './cellRenderers';
import {
  statusMessageValueGetter,
  dueInMessageValueGetter,
  EventDateTimeValueGetter,
  getRowDay,
  patientNameValueGetter,
  admitDayValueGetter,
} from './valueGetters';

export const commonColumnDefs = {
  wrapText: true,
  sortable: true,
  filterable: true,
  autoHeight: true,
  // flex: 1,
  resizable: true,
};

const numberCellStyle = { 'justify-content': 'end' };
const TextCellStyle = {
  'justify-content': 'start',
  display: 'inline-block',
  position: 'absolute',
  overflow: 'ellipsis',
};

const textWidth = 145;
const numberWidth = 70;

export const detailRowDefs = {
  detailCellRendererParams: {
    detailGridOptions: {
      suppressLoadingOverlay: true,
    },
  },
};

export const getColumnDefs = (
  palette: any,
  batchMode?: HopeBatchMode | null,
  patient?: mumms.patient,
) => {
  const PatientNumber = {
    headerName: defaults.number,
    field: 'patientnumber',
    width: 90,
    cellStyle: TextCellStyle,
    filter: true,
    pinned: 'left' as const,
  };

  const PatientName = {
    headerName: defaults.name,
    valueGetter: patientNameValueGetter,
    width: 150,
    cellStyle: TextCellStyle,
    filter: true,
  };

  const AdmitDay = {
    headerName: defaults.day,
    valueGetter: admitDayValueGetter,
    width: numberWidth,
    cellStyle: numberCellStyle,
  };

  // Decorative divider only — keep thin. Do not shrink the action (*Button) columns.
  const Spacer = {
    width: 4,
    minWidth: 4,
    maxWidth: 4,
    resizable: false,
    sortable: false,
    suppressMenu: true,
    wrapText: false,
    autoHeight: false,
    cellClass: 'no-pad',
    headerClass: 'no-pad',
    cellStyle: { backgroundColor: 'grey', padding: 0 },
  };
  const PinnedSpacer = {
    colId: 'pinnedSpacer',
    width: 4,
    minWidth: 4,
    maxWidth: 4,
    resizable: false,
    sortable: false,
    suppressMenu: true,
    wrapText: false,
    autoHeight: false,
    cellClass: 'no-pad',
    headerClass: 'no-pad',
    cellStyle: { backgroundColor: 'grey', padding: 0 },
    pinned: 'left',
  };

  const DaysSinceAdmit = {
    headerName: defaults.day,
    valueGetter: getRowDay,
    width: 75,
    pinned: 'left',
  };

  // Action column — leave at original width so icons/headers are not clipped
  const AdmitButton = {
    headerClass: 'button',
    width: 20,
    field: AdmitData,
    cellRendererFramework: ButtonCellRenderer,
    cellStyle: (props: any) => getButtonCellStyle(props, palette, patient),
  };

  const AdmitEventTime = {
    headerName: '',
    width: 60,
    cellStyle: (props: any) => getTextCellStyle(props, palette),
    field: AdmitData,
    valueGetter: (row: any) =>
      EventDateTimeValueGetter(row.data.admitdata, row?.data?.summary),
  };

  const Admit = {
    headerName: defaults.admit,
    valueGetter: (row: any) => statusMessageValueGetter(row.data.admitdata),
    field: AdmitData,
    cellStyle: (props: any) => getTextCellStyle(props, palette),
    width: textWidth,
    displayStatus: true,
  };

  const AdmitDueIn = {
    headerName: defaults.dueIn,
    valueGetter: (row: any) => dueInMessageValueGetter(row.data.admitdata),
    field: AdmitData,
    suppressMenu: true,
    cellStyle: (props: any) => getNumberCellStyle(props, palette),
    width: numberWidth,
  };

  // HUV1
  const HUV1Button = {
    headerClass: 'button',
    width: 20,
    field: HUV1Data,
    cellRendererFramework: ButtonCellRenderer,
    cellStyle: (props: any) => getButtonCellStyle(props, palette, patient),
  };

  const HUV1EventTime = {
    headerName: '',
    width: 60,
    cellStyle: (props: any) => getTextCellStyle(props, palette),
    field: HUV1Data,
    valueGetter: (row: any) =>
      EventDateTimeValueGetter(row.data.huv1Data, row?.data?.summary),
  };

  const HUV1 = {
    headerName: defaults.huv1,
    valueGetter: (row: any) => statusMessageValueGetter(row.data.huv1Data),
    field: HUV1Data,
    cellStyle: (props: any) => getTextCellStyle(props, palette),
    width: textWidth,
    displayStatus: true,
  };

  const HUV1DueIn = {
    headerName: defaults.dueIn,
    valueGetter: (row: any) => dueInMessageValueGetter(row.data.huv1Data),
    field: HUV1Data,
    cellStyle: (props: any) => getNumberCellStyle(props, palette),
    width: numberWidth,
    suppressMenu: true,
  };

  // HUV2
  const HUV2Button = {
    headerClass: 'button',
    width: 20,
    field: HUV2Data,
    cellRendererFramework: ButtonCellRenderer,
    cellStyle: (props: any) => getButtonCellStyle(props, palette, patient),
  };

  const HUV2EventTime = {
    headerName: '',
    width: 60,
    cellStyle: (props: any) => getTextCellStyle(props, palette),
    field: HUV2Data,
    valueGetter: (row: any) =>
      EventDateTimeValueGetter(row.data.huv2data, row?.data?.summary),
  };

  const HUV2 = {
    headerName: defaults.huv2,
    valueGetter: (row: any) => statusMessageValueGetter(row.data.huv2data),
    field: HUV2Data,
    cellStyle: (props: any) => getTextCellStyle(props, palette),
    width: textWidth,
    displayStatus: true,
  };

  const HUV2DueIn = {
    headerName: defaults.dueIn,
    valueGetter: (row: any) => dueInMessageValueGetter(row.data.huv2data),
    field: HUV2Data,
    cellStyle: (props: any) => getNumberCellStyle(props, palette),
    width: numberWidth,
    suppressMenu: true,
  };

  // Discharge
  const DischargeButton = {
    headerClass: 'button',
    width: 20,
    field: DischargeData,
    cellRendererFramework: ButtonCellRenderer,
    cellStyle: (props: any) => getButtonCellStyle(props, palette, patient),
  };

  const DischargeEventTime = {
    headerName: '',
    width: 60,
    cellStyle: (props: any) => getTextCellStyle(props, palette),
    field: DischargeData,
    valueGetter: (row: any) =>
      EventDateTimeValueGetter(row.data.dischargedata, row?.data?.summary),
  };

  const Discharge = {
    headerName: defaults.discharge,
    valueGetter: (row: any) => statusMessageValueGetter(row.data.dischargedata),
    field: DischargeData,
    cellStyle: (props: any) => getTextCellStyle(props, palette),
    width: textWidth,
    displayStatus: true,
  };

  const DischargeDueIn = {
    headerName: defaults.dueIn,
    valueGetter: (row: any) => dueInMessageValueGetter(row.data.dischargedata),
    field: DischargeData,
    cellStyle: (props: any) => getNumberCellStyle(props, palette),
    width: numberWidth,
    suppressMenu: true,
  };

  // [DetailColumnDefs, SummaryColumnDefs, SmallSummaryColumnDefs]

  const detailColumnDefs = [
    DaysSinceAdmit,
    PinnedSpacer,
    AdmitButton,
    AdmitEventTime,
    Admit,
    AdmitDueIn,
    Spacer,
    HUV1Button,
    HUV1EventTime,
    HUV1,
    HUV1DueIn,
    Spacer,
    HUV2Button,
    HUV2EventTime,
    HUV2,
    HUV2DueIn,
    Spacer,
    DischargeButton,
    DischargeEventTime,
    Discharge,
    DischargeDueIn,
  ];

  const summaryColumnDefs = [
    batchMode && {
      headerName: '',
      field: 'checkbox',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 30,
      pinned: 'left' as const,
    },
    PatientNumber,
    PatientName,
    AdmitDay,
    Spacer,
    AdmitButton,
    AdmitEventTime,
    Admit,
    AdmitDueIn,
    Spacer,
    HUV1Button,
    HUV1EventTime,
    HUV1,
    HUV1DueIn,
    Spacer,
    HUV2Button,
    HUV2EventTime,
    HUV2,
    HUV2DueIn,
    Spacer,
    DischargeButton,
    DischargeEventTime,
    Discharge,
    DischargeDueIn,
  ].filter((el: any) => !!el) as ColDef[];

  const smallSummaryColumnDefs = [
    PatientNumber,
    AdmitDay,
    Spacer,
    Admit,
    AdmitDueIn,
    Spacer,
    HUV1,
    HUV1DueIn,
    Spacer,
    HUV2,
    HUV2DueIn,
    Spacer,
    Discharge,
    DischargeDueIn,
  ];

  return [detailColumnDefs, summaryColumnDefs, smallSummaryColumnDefs];
};
